import {
  WS_EVENTS,
  type NotificationMessage,
  type NotificationType,
  type AchievementAwardedMessage,
  ENVIRONMENT,
} from "@repo/shared";
import { useEffect, useState } from "react";
import { match, P } from "ts-pattern";

import { useToast } from "~/components/ui/use-toast";
import { GamificationToast } from "~/modules/Client/Gamification/components/GamificationToast";

import { useWebSocket } from "../hooks/useWebSocket";

import type { ToastVariants } from "./ui/toast";

export const NotificationListener = () => {
  const { subscribe } = useWebSocket();
  const { toast } = useToast();
  const [achievement, setAchievement] = useState<AchievementAwardedMessage | null>(null);

  useEffect(() => {
    if (!subscribe) {
      return;
    }

    const unsubNotification = subscribe<NotificationMessage>(WS_EVENTS.NOTIFICATION, (data) => {
      toast({
        variant: mapNotificationType(data.type),
        description: data.message || "Otrzymano powiadomienie",
        duration: 5000,
      });
    });

    const unsubSystemNotification = subscribe<NotificationMessage>(
      WS_EVENTS.SYSTEM_NOTIFICATION,
      (data) => {
        toast({
          variant: mapNotificationType(data.type || "warning"),
          description: data.message || "Powiadomienie systemowe",
          duration: 5000,
        });
      },
    );

    const unsubAchievementAwarded = subscribe<AchievementAwardedMessage>(
      WS_EVENTS.ACHIEVEMENT_AWARDED,
      (data) => {
        if (data.badge && import.meta.env.VITE_ENVIRONMENT !== ENVIRONMENT.PRODUCTION) {
          setAchievement(data);
        }
      },
    );

    return () => {
      if (unsubNotification) unsubNotification();
      if (unsubSystemNotification) unsubSystemNotification();
      if (unsubAchievementAwarded) unsubAchievementAwarded();
    };
  }, [subscribe, toast]);

  return (
    <>
      {achievement && (
        <GamificationToast badge={achievement.badge} onDismiss={() => setAchievement(null)} />
      )}
    </>
  );
};

const mapNotificationType = (type?: NotificationType) => {
  return match(type)
    .returnType<Partial<ToastVariants["variant"]>>()
    .with("error", () => "destructive")
    .with("warning", () => "warning")
    .with("info", () => "info")
    .with(P.nullish, "success", () => "default")
    .exhaustive();
};
